<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore.js";
import { library } from "@common/library/library";

const editorSettings = {};

const componentPreviewConfig = {
    cId: "componentPreview",
    cType: "componentPreview",
    cTitle: "Component Preview",
    cTemplate: `<div class="component-preview" :data-test-id="testStoreId">
                    <div class="component-preview-enable" v-if="!previewEnabled">
                        <control name="ENABLE_PREVIEW"></control>
                    </div>
                    <div v-if="previewEnabled" :class="{'position-indicators-enabled': enablePositionIndicators}">
                        <div v-if="selectedComponent" class="component-preview-header">
                            <div class="component-preview-info">
                                Title: {{selectedComponent.cTitle}}<br/>
                                cId: {{componentId}}<br/>
                                <fieldset v-if="jiraTicketInfo">
                                    <legend>JIRA Ticket Information</legend>
                                    URL: <a :href="jiraTicketInfo.url" target="_blank">{{jiraTicketInfo.title}}</a><br/>
                                    ID: {{jiraTicketInfo.ID}}<br/>
                                    Project: {{jiraTicketInfo.project}}<br/>
                                    Sprints: {{jiraTicketInfo.sprints}}<br/>
                                </fieldset>
                            </div>
                            <div class="component-preview-controls">
                                <control name="DISABLE_PREVIEW"  v-if="previewEnabled"></control>
                                <control name="RELOAD_PREVIEW"></control>
                                <control v-if="componentsPassed" name="PREV_COMPONENT"></control>
                                <control v-if="componentsPassed" name="NEXT_COMPONENT"></control>
                            </div>
                        </div>
                        <div v-if="!selectedComponent && !previewReloading">
                            No component to preview.
                        </div>
                        <div v-if="!selectedComponent && previewReloading">
                            Loading component preview...
                        </div>
                        <div v-if="selectedComponent && !previewReloading">
                            <div v-if="selectedComponent.pages" class="component-preview-area">
                                <component :is="selectedComponent.cType" :unique-store-id="testStoreId"  :pageName="currentPage" :key="$route.fullPath" :editor-enabled="enableEditorInPreview" :prop-test-mode="testMode"></component>
                            </div>
                            <div v-else class="component-preview-area">
                                <component
                                    :is="selectedComponent.cType"
                                    :is-parent="true"
                                    :unique-store-id="testStoreId"
                                    :cId="selectedComponent.cId"
                                    :pcId="selectedComponent.cId"
                                    v-bind="mappedProps"
                                    :editor-enabled="enableEditorInPreview"
                                    :prop-test-mode="testMode"
                                    :context-vol-id="volId"
                                    :context-opp-id="oppId"
                                    :context-org-id="orgId"
                                    :context-lbe-id="lbeId"
                                ></component>
                            </div>
                        </div>
                    </div>
                </div>`,
    controls: [
        {
            name: 'ENABLE_PREVIEW',
            type: 'button',
            label: 'Enable Preview',
            onclick: 'togglePreview'
        },
        {
            name: 'DISABLE_PREVIEW',
            type: 'button',
            label: 'Disable Preview',
            onclick: 'togglePreview'
        },
        {
            name: 'RELOAD_PREVIEW',
            type: 'button',
            label: 'Reload',
            onclick: 'reloadComponent',
            cssClass: "e-block e-info",
        },
        {
            name: 'PREV_COMPONENT',
            type: 'button',
            label: 'Previous',
            onclick: 'prevComponent',
            cssClass: "e-block e-info",
        },
        {
            name: 'NEXT_COMPONENT',
            type: "button",
            label: "Next",
            onclick: "nextComponent",
            cssClass: "e-block e-info",
        }
    ],
    layoutDatamap: [
        {
            "property": "currentComponentId", "from": "componentPreview", "to": "parentStore", "as": "selectedComponentId"
        }
    ]
};

let componentPreviewCmp = {
    editorSettings: editorSettings,
    name: 'componentPreview',
    props: {
        componentId: {
            default: false
        },
        components: {
            default: {}
        },
        testStoreId: {
            type: String,
            default: 'devdash_test_window'
        },
        enableEditorInPreview: {
            type: Boolean,
            default: false
        },
        enablePositionIndicators: {
            type: Boolean,
            default: false
        }
    },
    globalMethods: ['selectComponent'],
    data() {
        return {
            componentConfig: undefined,
            currentComponentId: false,
            mappedProps: {},
            previewEnabled: false,
            previewReloading: false,
        };
    },
    computed: {
        currentPage() {
            return this.$route.fullPath.substring(1);
        },
        selectedComponent() {
            if (!this.previewEnabled || !this.componentId) return null;
            return this.componentConfig;
        },
        componentsPassed() {
            let componentsPassed = true;
            if ((Array.isArray(this.components) && !this.components.length) || (library.objIsEmpty(this.components))) {
                componentsPassed = false;
            }
            return componentsPassed;
        },
        jiraTicketInfo() {
            return errorHandler.get('jiraTicketInfo');
        }
    },
    watch: {
        componentId: async function () {
            this.previewReloading = true;
            this.updateState().then(() => {
                this.previewReloading = false;
            });
        }
    },
    methods: {
        selectComponent(component) {
            this.componentId = component.cId;
            this.updateState();
        },
        async updateState() {
            if (!this.componentId) {
                // if (this.componentConfig) this.componentConfig = undefined;
                return;
            }
            let cId = this.componentId;
            let tmpConfig = await componentStore.getConfig(cId);
            const config = library.cloneObj(tmpConfig);
            if (config.cType !== 'layoutContainer') {
                config.cId = cId;
            }
            config.loaded = true;
            this.componentConfig = library.cloneObj(config);
            // Load Test Data
            // TODO: move this to a separate library function
            let cType = this.componentConfig.cType;
            if (typeof testData === 'undefined' || typeof testData[cType] === 'undefined') return;
            this.mappedProps = {};
            let tmpState = {};
            for (const i in testData[cType]) {
                if (i === 'length') continue;
                let propName = 'prop' + library.capitalizeFirstLetter(i);
                if (i === 'cId' || i === 'config') propName = i;
                this.mappedProps[propName] = testData[cType][i][cType];
                tmpState[i] = testData[cType][i][cType];
            }
            if (this.previewEnabled) {
                if (this.layoutId) {
                    let interval = setInterval(() => {
                        if (typeof this.$store.state[this.layoutId][this.componentId] !== 'undefined') {
                            clearInterval(interval);
                            library.mergeObj(this.$store.state[this.layoutId][this.componentId], tmpState);
                        }
                    }, 500); // NOSONAR
                } else {
                    let interval = setInterval(() => {
                        if (typeof this.$store.state[this.layoutId][this.componentId] !== 'undefined') {
                            clearInterval(interval);
                            library.mergeObj(this.$store.state[this.componentId], tmpState);
                        }
                    }, 500); // NOSONAR
                }
            }
        },
        reloadComponent: async function () {
            this.previewReloading = true;
            setTimeout(() => {
                this.previewReloading = false;
            }, 500);
        },
        prevComponent: function () {
            let components = [];
            for (let i in this.components) {
                if (i === 'length') continue;
                components.push(this.components[i].cId);
            }
            if (!components.length) return;
            const count = components.length - 1;
            const selectedcId = this.componentId;
            const index = components.indexOf(selectedcId) ? components.indexOf(selectedcId) - 1 : count;
            const prevcId = components[index];
            this.previewReloading = true;
            this.currentComponentId = prevcId;
            this.$nextTick(() => {
                this.previewReloading = false;
            });
        },
        nextComponent: function () {
            let components = [];
            for (let i in this.components) {
                if (i === 'length') continue;
                components.push(this.components[i].cId);
            }
            if (!components.length) return;
            const count = components.length - 1;
            const tmpcId = this.componentId;
            const index = components.indexOf(tmpcId) <= (count - 1) ? components.indexOf(tmpcId) + 1 : 0;
            const nextcId = components[index];
            this.previewReloading = true;
            this.currentComponentId = nextcId;
            this.$nextTick(() => {
                this.previewReloading = false;
            });
        },
        togglePreview: function () {
            this.previewEnabled = !this.previewEnabled;
            this.updateState();
        },
        async onCreate() {
            await this.updateState();
        }
    },
    components: {
        "ejs-button": defineAsyncComponent(() => import('@common/syncfusion/button')),
    }
};
componentStore.initComponent(componentPreviewCmp, componentPreviewConfig);
export default componentPreviewCmp;
</script>

<style scoped>
    .position-indicators-enabled :deep(.dynamic-position-identifier) {
        display: block;
    }
    .component-preview .component-preview-header {
        display: flex;
    }
    .component-preview .component-preview-info {
        font-size: 16px;
        flex-grow: 3;
    }
    .component-preview .component-preview-controls {
        margin-bottom: 10px;
        text-align: right;
        flex-grow: 7;
    }
    .component-preview .component-preview-controls > div {
        flex-grow: 3;
    }
    .component-preview  .component-preview-controls .control {
        width: 180px;
        display: inline-block;
    }

    .component-preview  .component-preview-enable {
        text-align: center;
        width: 300px;
        padding-bottom: 10px;
        margin: 100px auto auto auto;
    }

    .component-preview .component-preview-area {
        border: 5px solid #FF1493;
        min-height: 300px;
        position: relative;
        margin: 10px;
        padding: 5px;
    }
</style>
